import React from 'react';
import { FiHome, FiCheck, FiArrowLeft, FiGlobe } from 'react-icons/fi';
import { LoadingSpinner } from '../common/Loading';

const KycStep3 = ({ 
  formData, 
  files, 
  errors, 
  submitting,
  onInputChange, 
  onFileChange, 
  onPrevStep, 
  onSubmit,
  countries 
}) => {
  return (
    <div className="bg-gray-900 rounded-xl p-6">
      <h2 className="text-2xl font-bold text-white mb-2">🏠 Address Information</h2>
      <p className="text-gray-400 mb-6">Enter your current residential address</p>
      
      <div className="space-y-6">
        {/* Address Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-gray-300 mb-2">Address Line 1 *</label>
            <input
              type="text"
              name="address_line1"
              value={formData.address_line1}
              onChange={(e) => onInputChange('address_line1', e.target.value)}
              className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.address_line1 ? 'border-red-500' : 'border-gray-700'
              }`}
              placeholder="Street address"
            />
            {errors.address_line1 && (
              <p className="mt-2 text-sm text-red-400">{errors.address_line1}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-gray-300 mb-2">Address Line 2 (Optional)</label>
            <input
              type="text"
              name="address_line2"
              value={formData.address_line2}
              onChange={(e) => onInputChange('address_line2', e.target.value)}
              className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Apartment, suite, unit, etc."
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">City *</label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={(e) => onInputChange('city', e.target.value)}
              className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.city ? 'border-red-500' : 'border-gray-700'
              }`}
              placeholder="City"
            />
            {errors.city && (
              <p className="mt-2 text-sm text-red-400">{errors.city}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-300 mb-2">State/Province *</label>
            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={(e) => onInputChange('state', e.target.value)}
              className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.state ? 'border-red-500' : 'border-gray-700'
              }`}
              placeholder="State or province"
            />
            {errors.state && (
              <p className="mt-2 text-sm text-red-400">{errors.state}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Postal Code *</label>
            <input
              type="text"
              name="postal_code"
              value={formData.postal_code}
              onChange={(e) => onInputChange('postal_code', e.target.value)}
              className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.postal_code ? 'border-red-500' : 'border-gray-700'
              }`}
              placeholder="Postal/ZIP code"
            />
            {errors.postal_code && (
              <p className="mt-2 text-sm text-red-400">{errors.postal_code}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-300 mb-2 flex items-center gap-2">
              <FiGlobe className="w-5 h-5" />
              Country *
            </label>
            <select
              name="country"
              value={formData.country}
              onChange={(e) => onInputChange('country', e.target.value)}
              className={`w-full bg-gray-800 text-white px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.country ? 'border-red-500' : 'border-gray-700'
              }`}
            >
              <option value="">Select Country</option>
              {countries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
            {errors.country && (
              <p className="mt-2 text-sm text-red-400">{errors.country}</p>
            )}
          </div>
        </div>

        {/* Proof of Address Upload */}
        <div>
          <label className="block text-gray-300 mb-3">
            <div className="flex items-center gap-2">
              <FiHome className="w-5 h-5" />
              Proof of Address *
            </div>
            <span className="text-xs text-gray-500">
              Utility bill, bank statement, or government letter (last 3 months)
            </span>
          </label>
          <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center bg-gray-800">
            <input
              type="file"
              onChange={(e) => onFileChange('proof_of_address', e.target.files[0])}
              accept="image/*,application/pdf"
              className="hidden"
              id="proof-address-upload"
            />
            <label htmlFor="proof-address-upload" className="cursor-pointer">
              <div className="flex flex-col items-center">
                <FiHome className="w-10 h-10 text-gray-400 mb-3" />
                {files.proof_of_address ? (
                  <>
                    <p className="text-green-400 font-medium mb-1">✓ File Selected</p>
                    <p className="text-gray-400 text-sm">{files.proof_of_address.name}</p>
                  </>
                ) : (
                  <>
                    <p className="text-gray-300 font-medium mb-1">Click to upload proof of address</p>
                    <p className="text-gray-500 text-sm">PDF, JPG, or PNG (Max 5MB)</p>
                  </>
                )}
              </div>
            </label>
            {errors.proof_of_address && (
              <p className="mt-3 text-sm text-red-400">{errors.proof_of_address}</p>
            )}
          </div>
        </div>

        {/* Review Summary */}
        <div className="bg-gray-800 rounded-lg p-4">
          <h3 className="font-bold text-white mb-3">📋 Review Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Document Type:</span>
              <span className="text-white">{formData.document_type?.replace('_', ' ') || 'Not set'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Document Country:</span>
              <span className="text-white">{formData.document_country || 'Not set'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Address:</span>
              <span className="text-white text-right">
                {formData.address_line1 ? `${formData.address_line1}, ${formData.city}` : 'Not set'}
              </span>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6 border-t border-gray-800">
          <button
            onClick={onPrevStep}
            disabled={submitting}
            className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiArrowLeft className="inline mr-2" />
            Back
          </button>
          <button
            onClick={onSubmit}
            disabled={submitting}
            className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <>
                <LoadingSpinner size="sm" color="white" />
                Submitting...
              </>
            ) : (
              <>
                <FiCheck className="w-5 h-5" />
                Submit KYC
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default KycStep3;